<!-- AI:
Status: 'in progress'
Type: Component
SubType: ObjectBaseModeComponent
Reason: Full view template of the lg.LogicMappingTable object component — navigation rail plus the four
        sections (master data + warnings + withdrawal, entries, partner overrides, quick test).
-->

@if (initialized && dto) {

    <!-- Navigation rail. Owned by THIS view: the object page host renders none, and the side view is the
         one that hands its items to the drawer host instead (see the component TSDoc). -->
    <div class="grid">
        <div class="col-12">
            <object-component-navigation-side
                    [name]="dto?.name || 'Mapping-Tabelle'"
                    [navigationItems]="navigationItems"
                    [(activeNavigationItem)]="activeNavigationItem">
            </object-component-navigation-side>
        </div>
    </div>

    <!-- ============================= Overview ============================ -->
    @if (activeNavigationItem?.action === 'overview') {

        <div class="grid">

            <!-- Configuration notes: the three traps of this entity, ahead of the fields themselves. -->
            <div class="col-12">

                @if (isTableInactive()) {
                    <div class="surface-card border-round border-left-3 border-orange-500 p-3 mb-3 flex align-items-start gap-3">
                        <i class="fa-solid fa-circle-pause text-orange-600 mt-1"></i>
                        <div class="flex flex-column gap-1">
                            <span class="font-semibold">
                                <p-tag value="Inaktiv" severity="warn" styleClass="mr-2"></p-tag>
                                Diese Wertetabelle ist deaktiviert
                            </span>
                            <span class="text-color-secondary text-sm">
                                Sie kann <strong>nicht mehr neu zugeordnet</strong> werden. Bereits
                                bestehende Zuordnungen laufen <strong>unverändert weiter</strong> — die
                                Auflösung ist davon nicht betroffen. Zum Reaktivieren das Kennzeichen
                                „Aktiv“ in den Stammdaten wieder setzen.
                            </span>
                        </div>
                    </div>
                }

                @if (isFallbackBehaviourMissing()) {
                    <div class="surface-card border-round border-left-3 border-yellow-500 p-3 mb-3 flex align-items-start gap-3">
                        <i class="fa-solid fa-triangle-exclamation text-yellow-600 mt-1"></i>
                        <div class="flex flex-column gap-1">
                            <span class="font-semibold">Kein Fallback-Verhalten gesetzt — die Tabelle wirkt wie „Fehler“</span>
                            <span class="text-color-secondary text-sm">
                                Ein leeres Fallback-Verhalten wird zur Laufzeit wie <strong>Fehler</strong> behandelt:
                                Die Tabelle bricht bei <strong>jedem</strong> Nichttreffer mit einer Ausnahme ab.
                                Das ist fast immer ein Versehen. Bitte das Feld „Fallback-Verhalten“ unten
                                ausdrücklich setzen — auch dann, wenn „Fehler“ tatsächlich gewollt ist.
                            </span>
                        </div>
                    </div>
                }

                <div class="surface-card border-round border-left-3 border-blue-500 p-3 mb-3 flex align-items-start gap-3">
                    <i class="fa-regular fa-circle-info text-blue-600 mt-1"></i>
                    <div class="flex flex-column gap-1">
                        <span class="font-semibold">Der Vergleich der Einträge ist zeichengenau</span>
                        <span class="text-color-secondary text-sm">
                            Ein Quellwert trifft einen Eintrag nur bei <strong>exakter</strong> Übereinstimmung.
                            Es wird weder Groß- und Kleinschreibung ausgeglichen noch werden führende oder
                            abschließende Leerzeichen entfernt, und es gibt keine Platzhalter oder Bereiche.
                            „DE“, „de“ und „DE “ sind drei verschiedene Quellwerte.
                        </span>
                    </div>
                </div>

            </div>

            <!-- Master data, rendered from the entity metadata. -->
            <div class="col-12">
                @if (widgetOverview) {
                    <mvs-widget [widgetData]="widgetOverview"></mvs-widget>
                }
                <!-- Quiet note next to the field the widget locks to READ. Locked rather than hidden:
                     the column can already carry a value on existing rows. -->
                <div class="text-color-secondary text-xs mt-2">
                    Das Feld „Ziel-Objekttyp“ ist nicht bearbeitbar: Es wird zur Laufzeit nirgends
                    ausgewertet. Auflösungen liefern immer eine Zeichenkette, nie eine Entität.
                </div>
            </div>

            <!-- ==================== Retire instead of delete ==================== -->
            <div class="col-12">
                <div class="surface-card shadow-1 border-round p-4 flex flex-column gap-3">

                    <div class="flex align-items-center gap-2">
                        <i class="fa-regular fa-shield-halved text-xl text-color-secondary"></i>
                        <h3 class="m-0 text-lg">Tabelle zurückziehen — nicht löschen</h3>
                    </div>

                    <p class="text-color-secondary text-sm m-0">
                        Diese Seite ist der erste Ort überhaupt, an dem eine Mapping-Tabelle gelöscht werden
                        kann. Ein Löschen ist hier aus gutem Grund nicht vorgesehen: Die Konfigurationstabellen
                        dieser Datenbank haben keine Fremdschlüssel-Beschränkungen. Eine gelöschte Tabelle
                        hinterlässt Flow-Schritte und Mapping-Logiken, die ins Leere zeigen — und eine einzige
                        solche Zeile kann dazu führen, dass eine ganze Liste im System leer zurückkommt.
                    </p>

                    <p class="text-color-secondary text-sm m-0">
                        <strong>Der vorgesehene Rückzugsweg ist das Deaktivieren</strong> über das
                        Kennzeichen „Aktiv“ in den Stammdaten oben. Was das genau bewirkt, ist wichtig:
                        Die Tabelle kann danach <strong>nicht mehr neu zugeordnet</strong> werden — sie
                        verschwindet aus der Auswahl. <strong>Bestehende Verwendungen laufen
                        unverändert weiter</strong> und lösen weiter gegen diese Tabelle auf; sie müssen
                        einzeln auf eine andere Tabelle umgehängt werden. Deaktivieren ist damit eine
                        Sperre für Neues, kein Abschalten des Bestands — und es ist jederzeit
                        umkehrbar, weil die Tabelle in der Konfigurationsliste sichtbar bleibt.
                    </p>

                    <div class="flex flex-wrap gap-2">
                        <p-button icon="fa-regular fa-list"
                                  label="Zu den Einträgen"
                                  [outlined]="true"
                                  size="small"
                                  (onClick)="activateNavigationItem('entries')"></p-button>
                        <p-button icon="fa-regular fa-user-gear"
                                  label="Zu den Partner-Overrides"
                                  [outlined]="true"
                                  size="small"
                                  (onClick)="activateNavigationItem('overrides')"></p-button>
                        <p-button icon="fa-regular fa-diagram-project"
                                  label="Verwendungen prüfen"
                                  [outlined]="true"
                                  size="small"
                                  [disabled]="dependentsLoading"
                                  (onClick)="loadDependents()"></p-button>
                    </div>

                    <!-- Result of the dependency check. The coverage note is ALWAYS shown next to it —
                         especially for an empty list, which is exactly when it gets misread. -->
                    @if (dependentsLoading) {
                        <p-skeleton height="2.5rem" styleClass="w-full"></p-skeleton>
                    }

                    @if (dependentsLoadError) {
                        <div class="flex align-items-start gap-2 text-sm">
                            <i class="fa-solid fa-triangle-exclamation text-red-500 mt-1"></i>
                            <span>
                                Die Verwendungen konnten nicht geladen werden. Das Ergebnis ist deshalb
                                <strong>unbekannt</strong> — nicht „keine Verwendungen“.
                            </span>
                        </div>
                    }

                    @if (dependentsLoaded && !dependentsLoading) {
                        @if (dependents.length) {
                            <div class="flex flex-column gap-2">
                                <span class="font-semibold text-sm">
                                    {{ dependents.length }} Verwendung(en) über eine Feldbeziehung gefunden:
                                </span>
                                <ul class="m-0 pl-4 text-sm">
                                    @for (reference of dependents; track reference.entityId) {
                                        <li>
                                            {{ getDependentLabel(reference) }}
                                            <span class="text-color-secondary">
                                                ({{ getDependentUsageLabel(reference) }})
                                            </span>
                                        </li>
                                    }
                                </ul>
                            </div>
                        } @else {
                            <span class="text-sm">
                                Keine Verwendung über eine Feldbeziehung gefunden.
                            </span>
                        }

                        <div class="surface-100 border-round p-3 flex align-items-start gap-2 text-sm">
                            <i class="fa-regular fa-circle-exclamation text-color-secondary mt-1"></i>
                            <span class="text-color-secondary">{{ dependencyCoverageNote }}</span>
                        </div>
                    }

                </div>
            </div>

        </div>
    }

    <!-- ============================= Entries ============================= -->
    @if (activeNavigationItem?.action === 'entries') {
        <div class="grid">
            <div class="col-12">
                <div class="surface-card border-round border-left-3 border-blue-500 p-3 mb-3 flex align-items-start gap-3">
                    <i class="fa-regular fa-circle-info text-blue-600 mt-1"></i>
                    <span class="text-color-secondary text-sm">
                        Quellwerte werden zeichengenau verglichen — ohne Ausgleich der Groß- und
                        Kleinschreibung und ohne Entfernen von Leerzeichen. Nur <strong>aktive</strong>
                        Einträge werden bei einer Auflösung berücksichtigt; die Reihenfolge bestimmt
                        „Priorität“.
                    </span>
                </div>
                @if (widgetEntries) {
                    <mvs-widget [widgetData]="widgetEntries"></mvs-widget>
                }
            </div>
        </div>
    }

    <!-- ======================== Partner overrides ======================== -->
    @if (activeNavigationItem?.action === 'overrides') {
        <div class="grid">
            <div class="col-12">
                <div class="surface-card border-round border-left-3 border-blue-500 p-3 mb-3 flex align-items-start gap-3">
                    <i class="fa-regular fa-circle-info text-blue-600 mt-1"></i>
                    <span class="text-color-secondary text-sm">
                        Partner-Overrides werden <strong>vor</strong> den Standardeinträgen geprüft und
                        greifen nur, wenn bei der Auflösung ein Partner bekannt ist. Auch hier zählen nur
                        <strong>aktive</strong> Zeilen.
                    </span>
                </div>
                @if (widgetOverrides) {
                    <mvs-widget [widgetData]="widgetOverrides"></mvs-widget>
                }
            </div>
        </div>
    }

    <!-- =========================== Quick test =========================== -->
    @if (activeNavigationItem?.action === 'quickTest') {
        <div class="grid">
            <div class="col-12">
                <!-- Binding contract: selector and inputs are agreed with the component specialist.
                     The component itself is declared in lg.module.ts. -->
                <mvs-lg-mapping-quick-test
                        [mappingTableId]="dto.id"
                        [mappingTableDto]="dto">
                </mvs-lg-mapping-quick-test>
            </div>
        </div>
    }

}
